import { formatNumber, NumberType } from '@uniswap/conedison/format'
import { useWeb3React } from '@web3-react/core'
import { EmptyWalletModule } from 'components/EmptyWalletContent'
import Row from 'components/Row'
import { MouseoverTooltip } from 'components/Tooltip'
import { useToggleWalletDrawer } from 'components/WalletDropdown'
import { MultipoolPosition } from 'lib/multipool/position'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThemedText } from 'theme'
import { switchChain } from 'utils/switchChain'
import { hasURL } from 'utils/urlChecks'

import { PortfolioLogo } from '../PortfolioLogo'
import PortfolioRow, { PortfolioSkeleton, PortfolioTabWrapper } from '../PortfolioRow'
import { PositionInfo } from './cache'
import { usePriceValues } from './hooks'
import useMultiChainStrategies from './useMultiChainStrategies'

export default function Strategies({ account }: { account: string }) {
  const { positions, loading } = useMultiChainStrategies(account)
  const toggleWalletDrawer = useToggleWalletDrawer()

  if (!positions || loading) {
    return <PortfolioSkeleton />
  }

  if (positions?.length === 0) {
    return <EmptyWalletModule type="strategy" onNavigateClick={toggleWalletDrawer} />
  }

  return (
    <PortfolioTabWrapper>
      {positions.map((positionInfo) => (
        <PositionListItem key={positionInfo.multipool.address + positionInfo.chainId} positionInfo={positionInfo} />
      ))}
    </PortfolioTabWrapper>
  )
}

export function calculcateLiquidityValue(
  price0: number | undefined,
  price1: number | undefined,
  position: MultipoolPosition
) {
  if (!price0 || !price1) return undefined

  const [amount0, amount1] = position.getAmounts()
  const value0 = parseFloat(amount0.toExact()) * price0
  const value1 = parseFloat(amount1.toExact()) * price1
  return value0 + value1
}

function PositionListItem({ positionInfo }: { positionInfo: PositionInfo }) {
  const { chainId, position, multipool } = positionInfo

  const { priceA, priceB } = usePriceValues(positionInfo)
  const liquidityValue = calculcateLiquidityValue(priceA, priceB, position)

  const navigate = useNavigate()
  const toggleWalletDrawer = useToggleWalletDrawer()
  const { chainId: walletChainId, connector } = useWeb3React()
  const onClick = useCallback(async () => {
    if (walletChainId !== chainId) await switchChain(connector, chainId)
    toggleWalletDrawer()
    navigate('/dashboard/strategies')
  }, [walletChainId, chainId, connector, toggleWalletDrawer, navigate])

  const containsURL = useMemo(
    () =>
      [multipool.token0.name, multipool.token0.symbol, multipool.token1.name, multipool.token1.symbol].some(
        (testString) => hasURL(testString)
      ),
    [multipool]
  )

  if (containsURL) {
    return null
  }

  return (
    <PortfolioRow
      onClick={onClick}
      left={<PortfolioLogo chainId={chainId} currencies={[multipool.token0, multipool.token1]} />}
      title={
        <Row>
          <ThemedText.SubHeader fontWeight={500}>
            {multipool.token0.symbol} / {multipool.token1.symbol}
          </ThemedText.SubHeader>
        </Row>
      }
      right={
        <>
          <MouseoverTooltip
            placement="left"
            text={
              <div style={{ padding: '4px 0px' }}>
                <ThemedText.Caption>{`${formatNumber(
                  liquidityValue,
                  NumberType.PortfolioBalance
                )} liquidity`}</ThemedText.Caption>
              </div>
            }
          >
            <ThemedText.SubHeader fontWeight={500}>
              {formatNumber(liquidityValue ?? 0, NumberType.PortfolioBalance)}
            </ThemedText.SubHeader>
          </MouseoverTooltip>
        </>
      }
    />
  )
}
